import { parseDate } from "@internationalized/date";
import { format, startOfMonth } from "date-fns";

export const DATE_BASES = [
  "start",
  "finish",
  "scan",
  "invoiceAdded",
  "returnUpload",
] as const;

export type DateBasis = (typeof DATE_BASES)[number];

export const DEFAULT_DATE_BASIS: DateBasis = "start";

/** The routes list and the driver rollup are two views over the same filters. */
export type DashboardView = "routes" | "drivers";

/**
 * The four exception worklists, each a flat list of the rows behind one of the
 * summary's counts. They replace the routes list in place, and the URL names
 * them by their endpoint.
 */
export const WORKLISTS = [
  "scan-exceptions",
  "non-scanned",
  "invoice-exceptions",
  "returns",
] as const;

export type Worklist = (typeof WORKLISTS)[number];

/** The translation key each worklist's copy lives under: `worklists.<key>`. */
export const WORKLIST_KEY = {
  "scan-exceptions": "scanExceptions",
  "non-scanned": "nonScanned",
  "invoice-exceptions": "invoiceExceptions",
  returns: "returns",
} as const satisfies Record<Worklist, string>;

export const PAGE_SIZE = 50;

/** Rows per page in each drawer tab. */
export const DRAWER_PAGE_SIZE = 50;

export const DRAWER_TABS = [
  "summary",
  "stops",
  "items",
  "pieces",
  "returns",
  "proof",
] as const;

export type DrawerTab = (typeof DRAWER_TABS)[number];

export const DEFAULT_DRAWER_TAB: DrawerTab = "summary";

/**
 * The route the drawer is open on: warehouse and route id, because the service
 * repeats a route id across warehouses. It travels in the URL as
 * `warehouse:routeId` so a reload or a pasted link reopens the drawer.
 */
export type OpenRoute = { warehouse: number; routeId: string };

export function formatOpenRoute(route: OpenRoute): string {
  return `${route.warehouse}:${route.routeId}`;
}

export function parseOpenRoute(raw: unknown): OpenRoute | undefined {
  if (typeof raw !== "string") return undefined;
  const match = /^(\d+):(.+)$/.exec(raw);
  if (!match) return undefined;
  return { warehouse: Number(match[1]), routeId: match[2]! };
}

/**
 * Everything the URL carries. A key is absent when it is at its default, so a
 * bare `/deliver` and a reset filter bar are the same address. The date window
 * is the exception in spirit: its default depends on today, so it is resolved
 * by `resolveFilters` rather than written into the URL.
 */
export type DeliverSearch = {
  /** One warehouse (a `dlvr_tenants` id). Absent means the first the caller may see. */
  tenantId?: number;
  basis?: DateBasis;
  from?: string;
  to?: string;
  q?: string;
  driver?: string;
  route?: string;
  willCall?: true;
  view?: "drivers";
  /** A worklist standing in for the routes list. */
  worklist?: Worklist;
  /** Zero-based, matching the service. Absent means the first page. */
  page?: number;
  /** The route whose drawer is open, as `formatOpenRoute` writes it. */
  open?: string;
  /** The drawer tab. Absent means the first. */
  tab?: Exclude<DrawerTab, "summary">;
  /** Narrows the drawer's items, pieces and proof to one stop. */
  stop?: number;
};

/** The filters as the service takes them: nothing optional left to guess. */
export type ResolvedFilters = {
  tenantId?: number;
  basis: DateBasis;
  from: string;
  to: string;
  q?: string;
  driver?: string;
  route?: string;
  willCall: boolean;
};

function isDateString(raw: unknown): raw is string {
  if (typeof raw !== "string" || raw.length === 0) return false;
  try {
    parseDate(raw);
    return true;
  } catch {
    return false;
  }
}

function nonEmptyString(raw: unknown): string | undefined {
  return typeof raw === "string" && raw.trim().length > 0
    ? raw.trim()
    : undefined;
}

function isDateBasis(raw: unknown): raw is DateBasis {
  return DATE_BASES.includes(raw as DateBasis);
}

export function validateDeliverSearch(
  search?: Record<string, unknown>,
): DeliverSearch {
  const tenantId = Number(search?.["tenantId"]);
  const basis = search?.["basis"];
  const from = search?.["from"];
  const to = search?.["to"];
  const q = nonEmptyString(search?.["q"]);
  const driver = nonEmptyString(search?.["driver"]);
  const route = nonEmptyString(search?.["route"]);
  const page = Number(search?.["page"]);
  const worklist = search?.["worklist"];
  const open = parseOpenRoute(search?.["open"]);
  const tab = search?.["tab"];
  const stop = Number(search?.["stop"]);

  return {
    ...(Number.isInteger(tenantId) && tenantId > 0 ? { tenantId } : {}),
    ...(isDateBasis(basis) && basis !== DEFAULT_DATE_BASIS ? { basis } : {}),
    ...(isDateString(from) ? { from } : {}),
    ...(isDateString(to) ? { to } : {}),
    ...(q ? { q } : {}),
    ...(driver ? { driver } : {}),
    ...(route ? { route } : {}),
    ...(search?.["willCall"] === true || search?.["willCall"] === "true"
      ? { willCall: true as const }
      : {}),
    ...(search?.["view"] === "drivers" ? { view: "drivers" as const } : {}),
    ...(WORKLISTS.includes(worklist as Worklist)
      ? { worklist: worklist as Worklist }
      : {}),
    ...(Number.isInteger(page) && page > 0 ? { page } : {}),
    // The tab and stop only mean something inside an open drawer.
    ...(open ? { open: formatOpenRoute(open) } : {}),
    ...(open &&
    DRAWER_TABS.includes(tab as DrawerTab) &&
    tab !== DEFAULT_DRAWER_TAB
      ? { tab: tab as Exclude<DrawerTab, "summary"> }
      : {}),
    ...(open && Number.isInteger(stop) && stop >= 0 ? { stop } : {}),
  };
}

const DATE_FORMAT = "yyyy-MM-dd";

/**
 * The dashboard opens on the month to date: the first of this month through
 * today. `now` is a parameter so the window is testable.
 */
export function defaultWindow(now: Date): { from: string; to: string } {
  return {
    from: format(startOfMonth(now), DATE_FORMAT),
    to: format(now, DATE_FORMAT),
  };
}

export function resolveFilters(
  search: DeliverSearch,
  now: Date,
  defaultTenantId?: number,
): ResolvedFilters {
  const window = defaultWindow(now);
  // The dashboard is always about one warehouse. Which one the URL names, else
  // the first the caller may see -- and until the list of warehouses arrives
  // there is no such thing, which is why this can still be undefined.
  const tenantId = search.tenantId ?? defaultTenantId;
  return {
    ...(tenantId !== undefined ? { tenantId } : {}),
    basis: search.basis ?? DEFAULT_DATE_BASIS,
    from: search.from ?? window.from,
    to: search.to ?? window.to,
    ...(search.q ? { q: search.q } : {}),
    ...(search.driver ? { driver: search.driver } : {}),
    ...(search.route ? { route: search.route } : {}),
    willCall: search.willCall === true,
  };
}

/**
 * The query params every dashboard endpoint shares. Optional filters are left
 * out rather than sent empty, and `includeWillCall` only when on, so the
 * service's own defaults apply everywhere else.
 */
export function filterQuery(filters: ResolvedFilters) {
  return {
    ...(filters.tenantId !== undefined ? { tenantId: filters.tenantId } : {}),
    dateBasis: filters.basis,
    from: filters.from,
    to: filters.to,
    ...(filters.q ? { q: filters.q } : {}),
    ...(filters.driver ? { driver: filters.driver } : {}),
    ...(filters.route ? { route: filters.route } : {}),
    ...(filters.willCall ? { includeWillCall: true } : {}),
  };
}

/**
 * Whether two sets of filters ask the service the same question. Comparing the
 * resolved filters rather than the URLs is the point: pressing Search on an
 * unchanged filter bar rewrites the URL to spell out the default dates, which
 * is a different address for the same query.
 */
export function sameFilters(a: ResolvedFilters, b: ResolvedFilters): boolean {
  return (
    a.tenantId === b.tenantId &&
    a.basis === b.basis &&
    a.from === b.from &&
    a.to === b.to &&
    a.q === b.q &&
    a.driver === b.driver &&
    a.route === b.route &&
    a.willCall === b.willCall
  );
}
